import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import api from '../../api';

export default function InformeIvaCorreoPage() {
  const [cfg, setCfg] = useState(null);
  const [testTo, setTestTo] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get('/informes-iva/config/global');
    setCfg(data);
    setTestTo(data.emailOficina || '');
  }, []);

  useEffect(() => {
    load().catch((e) => toast.error(e.response?.data?.error || e.message));
  }, [load]);

  const save = async () => {
    if (!cfg) return;
    setBusy(true);
    try {
      await api.put('/informes-iva/config/global', {
        digestMode: cfg.digestMode,
        emailOficina: cfg.emailOficina,
        frescuraHoras: cfg.frescuraHoras,
        autoSyncOnRun: cfg.autoSyncOnRun,
        syncF29OnRun: cfg.syncF29OnRun,
        scheduledRunsEnabled: cfg.scheduledRunsEnabled,
        smtpHost: cfg.smtpHost,
        smtpPort: cfg.smtpPort,
        smtpUser: cfg.smtpUser,
        smtpFrom: cfg.smtpFrom,
        smtpFromName: cfg.smtpFromName,
        smtpPass: cfg.smtpPass
      });
      toast.success('Configuración guardada');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.error || e.message);
    } finally {
      setBusy(false);
    }
  };

  const testMail = async () => {
    setBusy(true);
    try {
      await save();
      const { data } = await api.post('/informes-iva/config/global/test-email', { to: testTo });
      if (data.ok) toast.success('Correo de prueba enviado');
      else toast.error(data.reason || 'Error al enviar');
    } catch (e) {
      toast.error(e.response?.data?.error || e.response?.data?.reason || e.message);
    } finally {
      setBusy(false);
    }
  };

  if (!cfg) {
    return <div className="max-w-2xl mx-auto p-8 text-slate-500">Cargando…</div>;
  }

  return (
    <div className="max-w-2xl mx-auto px-4 py-10">
      <p className="text-sm text-slate-500 mb-1">
        <Link to="/informes-iva" className="text-indigo-600 hover:underline">
          ← Informes IVA
        </Link>
      </p>
      <h1 className="text-2xl font-semibold text-slate-900">Correo y envíos</h1>
      <p className="text-sm text-slate-600 mt-2 mb-8">
        SMTP para enviar informes al cliente. En el servidor: <code className="bg-slate-100 px-1 rounded">npm install</code> dentro de{' '}
        <code className="bg-slate-100 px-1 rounded">backend</code>.
      </p>

      <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-6 space-y-4 text-sm">
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">Servidor SMTP</label>
          <input
            className="w-full border border-slate-300 rounded-md px-3 py-2"
            value={cfg.smtpHost || ''}
            onChange={(e) => setCfg({ ...cfg, smtpHost: e.target.value })}
            placeholder="smtp.ejemplo.cl"
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Puerto</label>
            <input
              type="number"
              className="w-full border border-slate-300 rounded-md px-3 py-2"
              value={cfg.smtpPort ?? 587}
              onChange={(e) => setCfg({ ...cfg, smtpPort: Number(e.target.value) })}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Nombre remitente (visible en el inbox)</label>
            <input
              className="w-full border border-slate-300 rounded-md px-3 py-2"
              placeholder="Ej. Contabilidad OH — no es un correo"
              value={cfg.smtpFromName || ''}
              onChange={(e) => setCfg({ ...cfg, smtpFromName: e.target.value })}
            />
          </div>
          <div className="col-span-2">
            <label className="block text-xs font-medium text-slate-600 mb-1">Correo remitente (From)</label>
            <input
              type="email"
              className="w-full border border-slate-300 rounded-md px-3 py-2"
              placeholder="informes@suoficina.cl (obligatorio: debe incluir @ y dominio)"
              value={cfg.smtpFrom || ''}
              onChange={(e) => setCfg({ ...cfg, smtpFrom: e.target.value })}
            />
            <p className="text-xs text-slate-500 mt-1">
              Si lo deja vacío, se usa el usuario SMTP. No ponga aquí el nombre de la oficina (ej. &quot;Contabilidad&quot;).
            </p>
          </div>
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">Usuario SMTP</label>
          <input
            className="w-full border border-slate-300 rounded-md px-3 py-2"
            value={cfg.smtpUser || ''}
            onChange={(e) => setCfg({ ...cfg, smtpUser: e.target.value })}
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">Contraseña</label>
          <input
            type="password"
            className="w-full border border-slate-300 rounded-md px-3 py-2"
            placeholder={cfg.smtpPass === '********' ? '•••••••• (sin cambios)' : ''}
            onChange={(e) => setCfg({ ...cfg, smtpPass: e.target.value || '********' })}
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">CC oficina (copia en envíos)</label>
          <input
            className="w-full border border-slate-300 rounded-md px-3 py-2"
            value={cfg.emailOficina || ''}
            onChange={(e) => setCfg({ ...cfg, emailOficina: e.target.value })}
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">Enviar prueba a</label>
          <input
            className="w-full border border-slate-300 rounded-md px-3 py-2"
            value={testTo}
            onChange={(e) => setTestTo(e.target.value)}
          />
        </div>
        <label className="flex items-center gap-2 text-slate-700">
          <input
            type="checkbox"
            checked={cfg.syncF29OnRun !== false}
            onChange={(e) => setCfg({ ...cfg, syncF29OnRun: e.target.checked })}
          />
          Al generar informe, leer formulario F29 en el SII (504, 562, 142…)
        </label>
      </div>

      <div className="flex flex-wrap gap-2 mt-6">
        <button
          type="button"
          disabled={busy}
          className="px-4 py-2 bg-slate-900 text-white rounded-md text-sm disabled:opacity-50"
          onClick={save}
        >
          Guardar
        </button>
        <button
          type="button"
          disabled={busy}
          className="px-4 py-2 border border-slate-300 rounded-md text-sm disabled:opacity-50"
          onClick={testMail}
        >
          Enviar prueba
        </button>
      </div>
    </div>
  );
}
